/* eslint-disable react/prop-types */
import React, { useMemo } from "react"
import type * as Polymorphic from "@radix-ui/react-polymorphic"
import clsx from "clsx"
import Spinner from "components/Spinner"

export interface ButtonProps {
  variant: `primary` | `secondary` | `tertiary`
  size: `small` | `default` | `large`
  fluid: boolean
  inline: boolean
  invert: boolean
  icon?: `loading`
}

const sizeStyles: Record<ButtonProps[`size`], string> = {
  small: `px-3 md:px-4 py-1 leading-snug text-sm tracking-wide`,
  default: `px-4 md:px-5 py-2 h-10 leading-normal text-sm tracking-wide`,
  large: `px-4 md:px-6 py-3 h-12 leading-normal tracking-wider`
}

const defaultColours: Record<ButtonProps[`variant`], string> = {
  primary: `bg-black text-white border-[var(--border-color,black)]`,
  secondary: `bg-white text-black border-[var(--border-color,black)]`,
  tertiary: `bg-black text-black`
}

const invertedColours: Record<ButtonProps[`variant`], string> = {
  primary: `bg-white text-black border-[var(--border-color,white)]`,
  secondary: `bg-black text-white border-[var(--border-color,white)]`,
  tertiary: `bg-white text-white`
}

type PolymorphicButton = Polymorphic.ForwardRefComponent<
  `button`,
  Partial<ButtonProps>
>

const Button = React.forwardRef(
  (
    {
      as: Component = `button`,
      className,
      variant = `primary`,
      size = `default`,
      fluid = false,
      type = `button`,
      inline = false,
      invert = false,
      disabled,
      icon,
      ...props
    },
    forwardedRef
  ) => {
    const spinnerColour = useMemo(() => {
      if (disabled) {
        return `#b8b8b8`
      }
      if (variant === `secondary`) {
        return `#000`
      }
      return `#fff`
    }, [disabled, variant])

    return (
      <Component
        {...(Component === `button` && { disabled })}
        className={clsx(
          `rounded border font-medium justify-center items-center disabled:cursor-not-allowed transition-all`,
          sizeStyles[size],
          {
            "hover:shadow-button active:bg-opacity-80 active:border-opacity-80 disabled:hover:shadow-none":
              variant === `primary` || variant === `secondary`,
            "disabled:bg-primary-mid-gray disabled:text-white disabled:border-primary-mid-gray":
              variant === `primary`,
            "disabled:text-primary-mid-gray disabled:border-primary-mid-gray":
              variant === `secondary`,
            "underline border-transparent bg-opacity-0 hover:bg-opacity-5 active:text-opacity-80 disabled:text-primary-mid-gray":
              variant === `tertiary`,
            [defaultColours[variant]]: !invert,
            [invertedColours[variant]]: invert,
            "w-full": fluid,
            "inline-flex": inline,
            flex: !inline
          },
          className
        )}
        {...props}
        ref={forwardedRef}
        {...(Component === `button` && { type })}
      >
        {icon && (
          <Spinner
            width={20}
            height={20}
            color={spinnerColour}
            strokeWidth={15}
          />
        )}
        {!icon && props.children}
      </Component>
    )
  }
) as PolymorphicButton

export default Button
